import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/errors";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user, refresh } = useAuth();
  const [state, setState] = useState(token ? "working" : "error");
  const [message, setMessage] = useState(token ? "" : "This link is missing its token.");

  useEffect(() => {
    if (!token) return;
    api.post("/auth/verify-email", { token })
      .then(() => { setState("ok"); refresh(); })
      .catch((err) => { setState("error"); setMessage(formatApiError(err)); });
  }, [token, refresh]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="flex-1 max-w-md mx-auto w-full px-4 py-16">
        <Card className="tactile-card p-8 rounded-md text-center" data-testid="verify-email-card">
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#00E676]">EMAIL CONFIRMATION</div>
          {state === "working" && (
            <div className="mt-6 flex flex-col items-center gap-3 text-[#A0A7B0]" data-testid="verify-email-working">
              <Loader2 className="h-8 w-8 animate-spin text-[#00E676]" /> Confirming your email…
            </div>
          )}
          {state === "ok" && (
            <div className="mt-6" data-testid="verify-email-success">
              <CheckCircle2 className="h-10 w-10 text-[#00E676] mx-auto" />
              <h1 className="font-display text-3xl font-bold tracking-tight mt-3">You're confirmed.</h1>
              <p className="text-sm text-[#A0A7B0] mt-2">Your dashboard is unlocked.</p>
              <Link to={user ? "/dashboard" : "/login"} className="btn-emerald inline-flex items-center justify-center rounded-md h-11 px-6 mt-6 text-sm font-semibold" data-testid="verify-email-continue">
                {user ? "Go to dashboard" : "Sign in"}
              </Link>
            </div>
          )}
          {state === "error" && (
            <div className="mt-6" data-testid="verify-email-error">
              <XCircle className="h-10 w-10 text-[#FF5C5C] mx-auto" />
              <h1 className="font-display text-3xl font-bold tracking-tight mt-3">Link didn't work</h1>
              <p className="text-sm text-[#A0A7B0] mt-2">{message}</p>
              <p className="text-sm text-[#A0A7B0] mt-4">
                {user ? <>Sign in and use <b className="text-[#F4F1EA]">Resend confirmation</b> on your dashboard.</> : <><Link to="/login" className="text-[#00E676] hover:underline">Sign in</Link> to request a new link.</>}
              </p>
            </div>
          )}
        </Card>
      </div>
      <Footer />
    </div>
  );
}
